import { memo } from 'react';
import {
  lineHeightStyle,
  styledComponent,
  isExistAnyUrologyExaminationData,
  defaultUrologyData,
} from '../../../../utils/helpers';
import ChiefComplainDeleteBtn from '../chiefComplainDeleteBtn';
import biliarySystemImg from '../../../../assets/images/biliarySystem.png';
import digestiveSystemImg from '../../../../assets/images/digestiveSystem.png';

const flexRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const UrologyExaminationView = ({
  prescriptionItems,
  selectedOnExamination,
  setSelectedOnExamination,
  isHistoryPage,
}) => {
  let { urologyExamination } = selectedOnExamination || {};
  urologyExamination = urologyExamination || {};

  const removeSpecificItemData = (field, subField) => {
    const updatedUrologyExamination = { ...urologyExamination };

    if (updatedUrologyExamination[field]) {
      updatedUrologyExamination[field] = {
        ...updatedUrologyExamination[field],
        [subField]: '',
      };

      if (
        !updatedUrologyExamination[field].status &&
        !updatedUrologyExamination[field].note
      ) {
        const defaults = defaultUrologyData();
        updatedUrologyExamination[field] =
          defaults && defaults[field]
            ? defaults[field]
            : { status: '', note: '' };
      }
    }

    setSelectedOnExamination({
      ...selectedOnExamination,
      urologyExamination: updatedUrologyExamination,
    });
  };

  const Title = styledComponent(
    prescriptionItems?.items?.['on-examination']?.itemStyle || {},
  );
  const Value = styledComponent(
    prescriptionItems?.items?.['on-examination']?.subItemStyle || {},
  );

  const renderField = (fieldKey, label) => {
    const field = urologyExamination[fieldKey];
    if (!field || (!field.status && !field.note)) return null;
    return (
      <li className="text-left" key={fieldKey}>
        {field.status ? (
          <>
            <div style={flexRow}>
              <span>
                <Title>{label}:</Title>{' '}
                <Value>{field.status}</Value>
              </span>
              {!isHistoryPage && (
                <ChiefComplainDeleteBtn
                  action={() => removeSpecificItemData(fieldKey, 'status')}
                  itemName="status"
                />
              )}
            </div>
            {field.note && (
              <div style={flexRow}>
                <Value>- {field.note}</Value>
                {!isHistoryPage && (
                  <ChiefComplainDeleteBtn
                    action={() => removeSpecificItemData(fieldKey, 'note')}
                    itemName="note"
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <div style={flexRow}>
            <span>
              <Title>{label}:</Title>{' '}
              <Value>{field.note}</Value>
            </span>
            {!isHistoryPage && (
              <ChiefComplainDeleteBtn
                action={() => removeSpecificItemData(fieldKey, 'note')}
                itemName="note"
              />
            )}
          </div>
        )}
      </li>
    );
  };

  const hasDREData =
    (urologyExamination.analSensation &&
      (urologyExamination.analSensation.status ||
        urologyExamination.analSensation.note)) ||
    (urologyExamination.analTone &&
      (urologyExamination.analTone.status ||
        urologyExamination.analTone.note)) ||
    (urologyExamination.size &&
      (urologyExamination.size.status || urologyExamination.size.note)) ||
    (urologyExamination.consistency &&
      (urologyExamination.consistency.status ||
        urologyExamination.consistency.note)) ||
    (urologyExamination.tenderness &&
      (urologyExamination.tenderness.status ||
        urologyExamination.tenderness.note)) ||
    (urologyExamination.surface &&
      (urologyExamination.surface.status ||
        urologyExamination.surface.note)) ||
    (urologyExamination.nodularity &&
      (urologyExamination.nodularity.status ||
        urologyExamination.nodularity.note)) ||
    (urologyExamination.analMucosa &&
      (urologyExamination.analMucosa.status ||
        urologyExamination.analMucosa.note)) ||
    (urologyExamination.upperBorder &&
      (urologyExamination.upperBorder.status ||
        urologyExamination.upperBorder.note)) ||
    (urologyExamination.medianSulcus &&
      (urologyExamination.medianSulcus.status ||
        urologyExamination.medianSulcus.note));

  const hasExternalGenitaliaData =
    (urologyExamination.eum &&
      (urologyExamination.eum.status || urologyExamination.eum.note)) ||
    (urologyExamination.phallus &&
      (urologyExamination.phallus.status ||
        urologyExamination.phallus.note)) ||
    (urologyExamination.antUrethra &&
      (urologyExamination.antUrethra.status ||
        urologyExamination.antUrethra.note)) ||
    (urologyExamination.scrutum &&
      (urologyExamination.scrutum.status ||
        urologyExamination.scrutum.note)) ||
    (urologyExamination.testes &&
      (urologyExamination.testes.status ||
        urologyExamination.testes.note)) ||
    (urologyExamination.epididymis &&
      (urologyExamination.epididymis.status ||
        urologyExamination.epididymis.note)) ||
    (urologyExamination.vas &&
      (urologyExamination.vas.status || urologyExamination.vas.note)) ||
    (urologyExamination.pampiniformPlexus &&
      (urologyExamination.pampiniformPlexus.status ||
        urologyExamination.pampiniformPlexus.note));

  return (
    <>
      {isExistAnyUrologyExaminationData(urologyExamination) && (
        <>
          {hasDREData && (
            <div className="urology-sub-title">Digital Rectal Exam</div>
          )}
          <ul style={lineHeightStyle('on-examination', prescriptionItems)}>
            {renderField('analSensation', 'Anal Sensation')}
            {renderField('analTone', 'Anal Tone')}
            {renderField('size', 'Prostate Size')}
            {renderField('consistency', 'Prostate Consistency')}
            {renderField('tenderness', 'Tenderness')}
            {renderField('surface', 'Surface')}
            {renderField('nodularity', 'Nodularity')}
            {renderField('analMucosa', 'Anal Mucosa')}
            {renderField('upperBorder', 'Upper Border')}
            {renderField('medianSulcus', 'Median Sulcus')}
          </ul>

          {hasExternalGenitaliaData && (
            <div className="urology-sub-title">External Genitalia</div>
          )}
          <ul style={lineHeightStyle('on-examination', prescriptionItems)}>
            {renderField('eum', 'EUM')}
            {renderField('phallus', 'Phallus')}
            {renderField('antUrethra', 'Ant Urethra')}
            {renderField('scrutum', 'Scrutum')}
            {renderField('testes', 'Testes')}
            {renderField('epididymis', 'Epididymis')}
            {renderField('vas', 'Vas')}
            {renderField('pampiniformPlexus', 'Pampiniform Plexus')}

            {urologyExamination.showBiliaryPicture &&
              (urologyExamination.showBiliaryPicture.status ||
                urologyExamination.showBiliaryPicture.note) && (
                <li className="text-left">
                  <Title>Biliary System:</Title>{' '}
                  {urologyExamination.showBiliaryPicture.status && (
                    <div style={flexRow}>
                      <img
                        src={biliarySystemImg}
                        height={160}
                        width={200}
                        alt="Biliary System"
                      />
                      {!isHistoryPage && (
                        <ChiefComplainDeleteBtn
                          action={() =>
                            removeSpecificItemData('showBiliaryPicture', 'status')
                          }
                          itemName="image"
                        />
                      )}
                    </div>
                  )}
                  {urologyExamination.showBiliaryPicture.note && (
                    <div style={flexRow}>
                      <Value>
                        {urologyExamination.showBiliaryPicture.status
                          ? '- '
                          : ''}
                        {urologyExamination.showBiliaryPicture.note}
                      </Value>
                      {!isHistoryPage && (
                        <ChiefComplainDeleteBtn
                          action={() =>
                            removeSpecificItemData('showBiliaryPicture', 'note')
                          }
                          itemName="note"
                        />
                      )}
                    </div>
                  )}
                </li>
              )}

            {urologyExamination.showDigestivePicture &&
              (urologyExamination.showDigestivePicture.status ||
                urologyExamination.showDigestivePicture.note) && (
                <li className="text-left">
                  <Title>Digestive System:</Title>{' '}
                  {urologyExamination.showDigestivePicture.status && (
                    <div style={flexRow}>
                      <img
                        src={digestiveSystemImg}
                        height={250}
                        width={200}
                        alt="Digestive System"
                      />
                      {!isHistoryPage && (
                        <ChiefComplainDeleteBtn
                          action={() =>
                            removeSpecificItemData(
                              'showDigestivePicture',
                              'status',
                            )
                          }
                          itemName="image"
                        />
                      )}
                    </div>
                  )}
                  {urologyExamination.showDigestivePicture.note && (
                    <div style={flexRow}>
                      <Value>
                        {urologyExamination.showDigestivePicture.status
                          ? '- '
                          : ''}
                        {urologyExamination.showDigestivePicture.note}
                      </Value>
                      {!isHistoryPage && (
                        <ChiefComplainDeleteBtn
                          action={() =>
                            removeSpecificItemData(
                              'showDigestivePicture',
                              'note',
                            )
                          }
                          itemName="note"
                        />
                      )}
                    </div>
                  )}
                </li>
              )}
          </ul>
        </>
      )}
    </>
  );
};

export default memo(UrologyExaminationView);
